/**
 * Custom PO (2026-11-02): the office can ALWAYS start a purchase order for a
 * job, whether or not the estimate has anything the system can order by
 * itself. "Create PO from the estimate" only copies parts that carry a vendor
 * part number; when there are none (job #102678613: the quote line "Check
 * valve, piping and fittings" is labor) the server answers
 * NO_ORDERABLE_LINES, and that used to be a dead end.
 *
 * A custom PO is a blank draft for the job (POST /api/purchase-orders). With
 * no vendor named, the server puts it on the main vendor (Reece, vendorRule.ts).
 * The office types the lines in, or picks them from the pricebook, and can
 * change the vendor before anything is sent.
 */
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { api } from "@/lib/api";

/** Create-from-estimate refusals that should offer a custom PO instead. */
const CUSTOM_PO_INSTEAD = new Set(["NO_ORDERABLE_LINES", "NO_SOURCE_ESTIMATE"]);

/** True when a create-from-estimate error means "start a custom PO instead". */
export function shouldOfferCustomPo(err: unknown): boolean {
    const code = (err as { body?: { error?: unknown } } | null)?.body?.error;
    return typeof code === "string" && CUSTOM_PO_INSTEAD.has(code);
}

/** Plain words for the office when the estimate has nothing to order by itself. */
export const NOTHING_TO_ORDER_WORDS =
    "Nothing on the quote can be ordered by itself. Start a custom PO and type in what you need.";

export function useStartCustomPo(onStarted: (poId: string) => void) {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: (jobId: string) => api.post<{ order?: { id: string } }>("/api/purchase-orders", { jobId }),
        onSuccess: (res, jobId) => {
            qc.invalidateQueries({ queryKey: ["job-purchase-orders", jobId] });
            qc.invalidateQueries({ queryKey: ["purchase-orders"] });
            qc.invalidateQueries({ queryKey: ["purchasing"] });
            const id = res?.order?.id;
            if (!id) return;
            toast.success("Custom PO started. Add what you need. Nothing was sent.");
            onStarted(id);
        },
        onError: (e: any) => {
            const code = e?.body?.error;
            if (code === "JOB_CANCELLED") toast.warning("This job is cancelled. You can't order parts for it.");
            else toast.error("Couldn't start the PO. Try again.");
        },
    });
}
